import { css } from "@emotion/react";
import React, { forwardRef } from "react";
import { PartialColorProps, useTheme } from "../theme";

export interface ChipProps
  extends
    PartialColorProps,
    Omit<React.ComponentPropsWithoutRef<"span">, "color"> {
  children: React.ReactNode;
  /**
   * Iconとして入れる要素を指定します。
   *
   * @default null
   */
  icon?: React.ReactNode;
  /**
   * 右側にIconとして入れる要素を指定します。
   *
   * @default null
   */
  endIcon?: React.ReactNode;
  /**
   * 大きさを指定します。
   *
   * @default "small"
   */
  size?: "small" | "medium";
  /**
   * 削除されました。
   */
  variant?: string;
  /**
   * 角を丸くします。
   */
  rounded?: boolean;
  /**
   * ホバー時にツールチップのような見た目で指定された文字列を表示します。
   * このようなコンポーネントでは文字数をあまり長くできないのでユーザーに不親切になりがちなので、
   * なるべく指定してユーザーを手助けしてください。
   * titleが指定された場合、cursorがhelpに変更されます。
   * Tooltipコンポーネントを利用するよりこちらの利用を優先するべきです。
   */
  title?: string;
  style?: React.CSSProperties;
  className?: string;
}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  (
    {
      color = "default",
      icon,
      endIcon,
      size = "small",
      children,
      rounded,
      title,
      onClick,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();

    const clickable = onClick !== undefined;
    return (
      <span
        ref={ref}
        title={title}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={onClick}
        css={[
          size === "small" ? theme.typography.caption : theme.typography.body,
          css`
            box-sizing: border-box;
            display: inline-flex;
            padding: 4px 12px;
            color: ${color === "default"
              ? theme.palette.text.primary
              : theme.palette.intentions[color]?.contrastText};
            white-space: nowrap;
            cursor: ${title ? "help" : "inherit"};
            background-color: ${color === "default"
              ? theme.palette.gray[200]
              : theme.palette.intentions[color]?.main};
            border-radius: ${rounded
              ? // 本当は高さの50%を指定するべきだが、sizeによって変わるので対応が面倒なため、とりあえずめちゃくちゃ大きい値を指定している
                "9999px"
              : "4px"};
            &[role="button"] {
              cursor: pointer;
            }
          `,
        ]}
        {...props}
      >
        {icon !== undefined && icon !== null && (
          <span
            css={css`
              display: grid;
              align-items: center;
              margin-right: 0.5em;
            `}
          >
            {icon}
          </span>
        )}
        {children}
        {endIcon !== undefined && icon !== null && (
          <span
            css={css`
              display: grid;
              align-items: center;
              margin-left: 0.5em;
            `}
          >
            {endIcon}
          </span>
        )}
      </span>
    );
  },
);
